import { ETypographyIntents } from '@src/typings';
import { switchVariants } from '@src/utilities/switchVariants';
import clsx from 'clsx';
import React, { forwardRef, useEffect, useState } from 'react';
import { useTheme, withTheme } from '../ThemeProvider';
import { Typography } from '../Typography';
import { StyledSwitch } from './style';
import { SwitchProps } from './types';

const SwitchComponent = forwardRef<HTMLDivElement, SwitchProps>(
    (
        {
            icon,
            label,
            size = 'md',
            labelSize = ETypographyIntents.Regular,
            iconSize = 'md',
            onChange,
            className,
            secondLabel,
            ...props
        },
        ref
    ) => {
        const [checked, setChecked] = useState(
            props.checked || props.value || false
        );
        const { onClick, defaultValue, ...rest } = props;
        const { theme } = useTheme();

        const handleChange = (checkedValue: boolean) => {
            setChecked(checkedValue);
            onChange?.(checkedValue);
        };

        useEffect(() => {
            setChecked(!!props.checked || !!props.value);
        }, [props.checked, props.value]);

        return (
            <div ref={ref} className={clsx('inline-flex items-center gap-2')}>
                <StyledSwitch
                    size={size}
                    checked={checked}
                    onChange={(checked: boolean) => {
                        handleChange(checked);
                    }}
                    className={clsx(switchVariants({ className, size }))}
                    {...rest}
                />
                {icon && (
                    <span
                        className={clsx(switchVariants({ size }))}
                        style={{
                            color: checked
                                ? theme.colors.primary.main.filled
                                : theme.colors.secondary.main.text,
                        }}
                    >
                        {typeof icon === 'function'
                            ? React.createElement(icon)
                            : icon}
                    </span>
                )}
                {label && (
                    <Typography
                        intent={labelSize}
                        className={clsx(switchVariants({ size }))}
                        style={{
                            color: checked
                                ? theme.colors.primary.main.filled
                                : theme.colors.secondary.main.text,
                        }}
                    >
                        {label}
                    </Typography>
                )}
                {secondLabel && (
                    <label className="text-sm">{secondLabel} </label>
                )}
            </div>
        );
    }
);

SwitchComponent.displayName = 'SwitchComponent';

export const Switch = withTheme(SwitchComponent);
